import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setStatus(s);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [sessionId]);

  const isComplete = status?.status === "complete" || status?.payment_status === "paid";

  return (
    <div
      data-testid="success-page"
      className="min-h-screen bg-ink flex items-center justify-center px-6 grain"
    >
      <div className="max-w-2xl w-full border border-white/10 bg-[#0F0F0F] p-12">
        <div className="data-label mb-6 text-gold">/ You're All Set</div>
        {error ? (
          <div className="text-red-400 font-mono text-sm" data-testid="success-error">
            {error}
          </div>
        ) : !status ? (
          <div className="flex items-center gap-3 text-white/70">
            <Loader2 className="animate-spin text-gold" size={20} />
            <span className="font-mono text-sm tracking-wider uppercase">Checking your payment…</span>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-8">
              <CheckCircle2 className="text-gold mt-1" size={32} />
              <div>
                <h1 className="font-display text-4xl sm:text-5xl leading-tight text-white">
                  {isComplete ? "Welcome to Axiom!" : "Finishing up your payment…"}
                </h1>
                <p className="mt-4 text-white/70 text-base">
                  {isComplete
                    ? "A senior advisor will email you in the next 24 hours to say hi and get you started."
                    : "We're confirming your payment. This page will update on its own."}
                </p>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-y-4 font-mono text-[12px] tracking-[0.2em] uppercase border-t border-white/10 pt-6">
              <dt className="text-white/40">Plan</dt>
              <dd className="text-white">{status.tier || "—"}</dd>
              <dt className="text-white/40">Billing</dt>
              <dd className="text-white">{status.cycle || "—"}</dd>
              <dt className="text-white/40">Status</dt>
              <dd className="text-gold">{status.status}</dd>
              <dt className="text-white/40">Email</dt>
              <dd className="text-white truncate">{status.customer_email || status.email || "—"}</dd>
            </dl>

            <Link
              to="/"
              data-testid="success-back-home"
              className="mt-10 inline-flex items-center gap-2 border border-gold/50 text-gold px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-ink transition-colors"
            >
              ← Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
